import { useEffect, FC } from 'react';
import { useForm } from 'react-hook-form';

import {
  initNotificationFormFields,
  type NotificationFormFields,
} from 'page/NotificationPage/types/notificationFormField';
import {
  useCustomerStore,
  useEditStore,
  useListStore,
} from 'page/NotificationPage/hooks';
import { InputModal } from './InputModal';
import { ConfirmationModal } from './ConfirmationModal';
import { DeleteModal } from './DeleteModal';

export const EditModal: FC = () => {
  const {
    init: initEdit,
    setForm,
    goConfirmationModal,
    goInputModal,
    goDeleteModal,
    putNotificationAndReload,
    deleteNotificationAndReload,
  } = useEditStore();
  const { init: initCustomers } = useCustomerStore();
  const { selectedNotification, clearSelectNotification } = useListStore();

  const { control, formState, register, getValues, reset } =
    useForm<NotificationFormFields>({
      defaultValues: initNotificationFormFields,
      mode: 'all',
    });

  useEffect(() => {
    if (!selectedNotification) {
      return;
    }
    reset({
      title: selectedNotification.title,
      message: selectedNotification.message,
      isHigh: selectedNotification.isHigh,
      startDate: selectedNotification.startsAt,
      endDate: selectedNotification.endsAt,
      condition: selectedNotification.condition,
    });
  }, [selectedNotification, reset]);

  /**
   * モーダルを閉じたときにおこなう一連の初期化処理
   */
  const initState = () => {
    // フォームをリセット
    reset();
    // 編集画面の state を初期化
    initEdit();
    // 顧客一覧の state を初期化
    initCustomers();
    // 選択されたお知らせをクリア
    clearSelectNotification();
  };

  /**
   * すべてのモーダルで右上の「×」ボタンをクリックしたときの処理
   */
  const handleClose = () => {
    // 初期化
    initState();
  };

  /**
   * 入力モーダルで「確認」ボタンをクリックしたときの処理
   */
  const handleClickGoConfirmation = () => {
    // フォームの内容を state に保存
    setForm(getValues());
    // 確認モーダルに進む
    goConfirmationModal();
  };

  /**
   * 入力モーダルで「削除」ボタンをクリックしたときの処理
   */
  const handleClickGoDelete = () => {
    // フォームの内容を state に保存
    setForm(getValues());
    // 削除モーダルに遷移する
    goDeleteModal();
  };

  /**
   * 入力モーダル、削除モーダルで「入力画面に戻る」ボタンをクリックしたときの処理
   */
  const handleClickGoInput = () => {
    // 入力モーダルに戻る
    goInputModal();
  };

  /**
   * 確認モーダルで「更新ボタン」をクリックしたときの処理
   */
  const handleClickUpdate = () => {
    // お知らせ更新処理＆一覧をリロード
    void putNotificationAndReload();
    // 初期化
    initState();
  };

  /**
   * 削除モーダルで「削除ボタン」をクリックしたときの処理
   */
  const handleClickDelete = () => {
    // お知らせ削除処理＆一覧をリロード
    void deleteNotificationAndReload();
    // 初期化
    initState();
  };

  return (
    <>
      {/* 入力モーダルコンテナ */}
      <InputModal
        control={control}
        formState={formState}
        register={register}
        getValues={getValues}
        handleClose={handleClose}
        handleClickGoDelete={handleClickGoDelete}
        handleClickGoConfirmation={handleClickGoConfirmation}
      />
      {/* 確認モーダルコンテナ */}
      <ConfirmationModal
        handleClose={handleClose}
        handleClickGoInput={handleClickGoInput}
        handleClickUpdate={handleClickUpdate}
      />
      {/* 削除モーダルコンテナ */}
      <DeleteModal
        handleClose={handleClose}
        handleClickGoInput={handleClickGoInput}
        handleClickDelete={handleClickDelete}
      />
    </>
  );
};
